import { FC, ReactElement, useEffect, useState } from "react"
import Input from "./Input"
import Button from "./Button"
import { Controller, useForm } from "react-hook-form"
import { NewsService, AddNewsRequestSchema } from "../sdk"
import useError from "../hooks/useError"
import classNames from "classnames"
import Checkbox from "./Checkbox"
import { dateToInputDatetime } from "../helpers/date"

type Props = {
  onSuccess?(): void
}

const Form: FC<Props> = ({ onSuccess }) => {
  const [startPublicDisabled, setStartPublicDisabled] = useState<boolean>(true)
  const [endPublicDisabled, setEndPublicDisabled] = useState<boolean>(true)

  const { handleSubmit, control, formState, getValues, setValue } = useForm<AddNewsRequestSchema>()
  const { handleError } = useError()

  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit = async({
    startPublicTimestamp,
    endPublicTimestamp,
    title,
    description
  }: AddNewsRequestSchema): Promise<void> => {
    setLoading(true)
    try {
      await NewsService.addNews({
        draft: false,
        startPublicTimestamp: (!startPublicDisabled && startPublicTimestamp)
          ? new Date(startPublicTimestamp).toISOString()
          : null,
        endPublicTimestamp: (!endPublicDisabled && endPublicTimestamp)
          ? new Date(endPublicTimestamp).toISOString()
          : null,
        title,
        description
      })
      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!startPublicDisabled && !getValues("startPublicTimestamp")) {
      setValue("startPublicTimestamp", dateToInputDatetime(new Date()))
    }
  }, [startPublicDisabled])

  useEffect(() => {
    if (!endPublicDisabled && !getValues("endPublicTimestamp")) {
      setValue("endPublicTimestamp", dateToInputDatetime(new Date()))
    }
  }, [endPublicDisabled])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classNames(loading && "opacity-50 pointer-events-none")}>
      <Controller
        name="title"
        control={control}
        defaultValue=""
        rules={{
          required: "Campo obbligatorio"
        }}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="text"
              label="Titolo"
              value={value}
              onChange={onChange}
              error={formState.errors.title?.message}
              className="mb-4"
            />
          )
        }}
      />

      <Controller
        name="description"
        control={control}
        defaultValue=""
        rules={{
          required: "Campo obbligatorio"
        }}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="text"
              label="Descrizione"
              value={value}
              onChange={onChange}
              error={formState.errors.description?.message}
              className="my-4"
            />
          )
        }}
      />

      <Checkbox
        label="Pubblica subito"
        checked={startPublicDisabled}
        onChange={(): void => setStartPublicDisabled(!startPublicDisabled)}
        className="ml-4"
        containerClassName="w-56"
        id="publicFromNow"
      />

      <Checkbox
        label="Nessuna data di scadenza"
        checked={endPublicDisabled}
        onChange={(): void => setEndPublicDisabled(!endPublicDisabled)}
        containerClassName="w-56"
      />

      <Controller
        control={control}
        name="startPublicTimestamp"
        defaultValue={""}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="datetime-local"
              onChange={onChange}
              value={value!}
              label="Inizio pubblicazione"
              className="my-4"
              disabled={startPublicDisabled}
            />
          )
        }}
      />

      <Controller
        control={control}
        name="endPublicTimestamp"
        defaultValue={""}
        render={({ field: { onChange, value } }): ReactElement => {
          return (
            <Input
              type="datetime-local"
              onChange={onChange}
              value={value!}
              label="Fine pubblicazione"
              className="mt-4"
              disabled={endPublicDisabled}
            />
          )
        }}
      />

      <Button type="submit" className="w-full mt-8">Salva</Button>
    </form>
  )
}

export default Form
